import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { IWorkspaceStatus } from 'src/models/workspace/WorkspaceStatus/types';
import { useDrop } from 'react-dnd';
import classNames from 'classnames';
import { useToggle } from 'react-use';
import { notification } from 'src/common';
import { IBidsBoardDndItem } from 'src/models/bids/Bids/types';
import { DndType } from 'src/types/dnd';
import { useUpdateBidStatus, getUpdateCacheOnUpdateBidStatus, useStagesBids } from 'src/models/bids/Bids/hooks';
import BoardColumnContent from './BoardColumnContent';
import BoardHeader from '../BoardHeader';
import BoardColumnFooter from './BoardColumnFooter';
import useBidsSearchArgs from 'src/reactiveVars/BidsSearchArgsVar';
import { trackChangeBidStage } from 'src/segment/events';

interface IProps {
  status: IWorkspaceStatus;
}

export const BoardColumn: FC<IProps> = ({ status }) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const [moveToWsStatus] = useUpdateBidStatus();
  const { data: bidsData } = useStagesBids({ amount: 10, stageId: status.id });

  const [isCollapsed, toggleIsCollapsed] = useToggle(false);
  const [{ rejected: isRejectedTab }] = useBidsSearchArgs();

  const { t } = useTranslation();

  const onToggleClick = useCallback(() => {
    toggleIsCollapsed();
  }, [toggleIsCollapsed]);

  const onDrop = useCallback(
    (item: IBidsBoardDndItem) => {
      if (!status) {
        console.warn('Can not drop to null status');
        return;
      }
      const { dndItem } = item;
      if (status.id === dndItem.status?.id) return;

      moveToWsStatus({
        variables: {
          bidId: dndItem.id,
          statusId: status.id
        },
        update: getUpdateCacheOnUpdateBidStatus({
          wsId: dndItem.workspace.id,
          bidId: dndItem.id,
          rejectedView: !!isRejectedTab,
          currentStatusId: status.id,
          prevStatusId: dndItem.status?.id
        })
      })
        .then(() => {
          trackChangeBidStage(
            {
              id: dndItem.workspace.id,
              name: dndItem.workspace.name
            },
            status.category,
            'Bispace kanban view (drag n drop)'
          );
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [isRejectedTab, moveToWsStatus, status, t]
  );

  const [{ isOver, item }, drop] = useDrop({
    accept: DndType.BidsBoardItem,
    canDrop: () => !!status,
    drop: onDrop,
    collect: monitor => ({
      isOver: monitor.isOver(),
      item: monitor.getItem()
    })
  });

  const isSameKanbanStatusOnDrop = useMemo(() => {
    return item ? item.dndItem?.status.id === status?.id : false;
  }, [item, status]);

  return (
    <>
      {!!status && (
        <div className={classNames(styles.kanbanItem, { [styles.isCollapsed]: isCollapsed })} ref={drop}>
          <div className={styles.kanbanItemInner} ref={setContainer}>
            <BoardHeader status={status} bidsCount={bidsData?.total || 0} onToggleClick={onToggleClick} />
            <BoardColumnContent
              isOver={isOver}
              isSameKanbanStatusOnDrop={isSameKanbanStatusOnDrop}
              stageId={status.id}
              container={container}
            />
            <BoardColumnFooter statusId={status.id} />
          </div>
        </div>
      )}
    </>
  );
};
export default BoardColumn;
